import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import {
  Lightbulb,
  Movie,
  Mic,
  Settings,
  PlayArrow,
  Palette,
} from '@mui/icons-material';

// Star Wars hero image - Luke vs Vader duel from Empire Strikes Back
const HERO_IMAGE = '/star-wars-hero.webp';

// Hero section inspired by Philips Hue holiday page
const HeroSection = ({ onGetStarted }) => (
  <Box
    sx={{
      position: 'relative',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      background: '#000',
    }}
  >
    {/* Ambient light glow effects - Star Wars blue and green */}
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `
          radial-gradient(ellipse 80% 50% at 50% 40%, rgba(68, 136, 255, 0.25) 0%, transparent 60%),
          radial-gradient(ellipse 60% 40% at 30% 60%, rgba(0, 200, 100, 0.15) 0%, transparent 50%),
          radial-gradient(ellipse 60% 40% at 70% 60%, rgba(68, 136, 255, 0.15) 0%, transparent 50%)
        `,
        zIndex: 1,
        pointerEvents: 'none',
      }}
    />

    {/* Main content container */}
    <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, textAlign: 'center', pt: { xs: 8, md: 4 } }}>
      {/* Badge */}
      <Typography
        sx={{
          display: 'inline-block',
          background: 'linear-gradient(135deg, rgba(68, 136, 255, 0.2) 0%, rgba(0, 200, 100, 0.2) 100%)',
          border: '1px solid rgba(68, 136, 255, 0.3)',
          borderRadius: '50px',
          px: 3,
          py: 1,
          mb: 4,
          color: '#4488ff',
          fontSize: '0.85rem',
          fontWeight: 600,
          letterSpacing: '2px',
          textTransform: 'uppercase',
        }}
      >
        Immersive Home Cinema
      </Typography>

      {/* Main headline */}
      <Typography
        variant="h1"
        sx={{
          fontSize: { xs: '2.8rem', sm: '4rem', md: '5rem', lg: '6rem' },
          fontWeight: 800,
          lineHeight: 1.05,
          mb: 3,
          color: '#fff',
          fontFamily: '"Outfit", "Inter", sans-serif',
          textShadow: '0 0 80px rgba(68, 136, 255, 0.5)',
        }}
      >
        Your Lights. Your Movie.
        <Box
          component="span"
          sx={{
            display: 'block',
            background: 'linear-gradient(90deg, #4488ff, #00cc66, #4488ff)',
            backgroundSize: '200% auto',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            animation: 'shimmer 3s linear infinite',
            '@keyframes shimmer': {
              '0%': { backgroundPosition: '0% center' },
              '100%': { backgroundPosition: '200% center' },
            },
          }}
        >
          Pure Magic.
        </Box>
      </Typography>

      {/* Subheadline */}
      <Typography
        variant="h5"
        sx={{
          color: 'rgba(255,255,255,0.7)',
          mb: 4,
          fontWeight: 400,
          lineHeight: 1.5,
          maxWidth: '700px',
          mx: 'auto',
          fontSize: { xs: '1rem', sm: '1.25rem', md: '1.4rem' },
        }}
      >
        Transform your living room into a cinematic experience.
        Hue Movie Themes sets your Philips Hue lights to match any movie—just say the name.
      </Typography>

      {/* CTA buttons */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        justifyContent="center"
        sx={{ mb: 6 }}
      >
        <Button
          variant="contained"
          size="large"
          onClick={onGetStarted}
          startIcon={<PlayArrow />}
          sx={{
            background: 'linear-gradient(135deg, #4488ff 0%, #00cc66 100%)',
            color: '#fff',
            fontWeight: 700,
            fontSize: '1.1rem',
            px: 5,
            py: 1.8,
            borderRadius: '50px',
            textTransform: 'none',
            boxShadow: '0 10px 40px rgba(68, 136, 255, 0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5599ff 0%, #00dd77 100%)',
              boxShadow: '0 15px 50px rgba(68, 136, 255, 0.5)',
              transform: 'translateY(-2px)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          Get Started Free
        </Button>
        <Button
          variant="outlined"
          size="large"
          sx={{
            borderColor: 'rgba(255,255,255,0.4)',
            color: '#fff',
            fontWeight: 600,
            fontSize: '1.1rem',
            px: 5,
            py: 1.8,
            borderRadius: '50px',
            textTransform: 'none',
            '&:hover': {
              borderColor: '#4488ff',
              background: 'rgba(68, 136, 255, 0.1)',
            },
          }}
        >
          Watch Demo
        </Button>
      </Stack>

      {/* TV with Star Wars scene */}
      <Box
        sx={{
          position: 'relative',
          maxWidth: '900px',
          mx: 'auto',
          mb: 4,
        }}
      >
        {/* Ambient light glow behind TV */}
        <Box
          sx={{
            position: 'absolute',
            top: '-20%',
            left: '-10%',
            right: '-10%',
            bottom: '-20%',
            background: `
              radial-gradient(ellipse 100% 80% at 50% 50%, rgba(68, 136, 255, 0.4) 0%, transparent 50%),
              radial-gradient(ellipse 50% 50% at 20% 80%, rgba(0, 200, 100, 0.3) 0%, transparent 40%),
              radial-gradient(ellipse 50% 50% at 80% 80%, rgba(68, 136, 255, 0.3) 0%, transparent 40%)
            `,
            filter: 'blur(40px)',
            zIndex: 0,
            animation: 'ambientPulse 4s ease-in-out infinite',
            '@keyframes ambientPulse': {
              '0%, 100%': { opacity: 0.8 },
              '50%': { opacity: 1 },
            },
          }}
        />

        {/* TV Frame */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            background: 'linear-gradient(180deg, #1a1a1a 0%, #0d0d0d 100%)',
            borderRadius: { xs: '12px', md: '16px' },
            p: { xs: '8px', sm: '12px', md: '16px' },
            boxShadow: '0 30px 80px rgba(0,0,0,0.8), inset 0 1px 0 rgba(255,255,255,0.1)',
          }}
        >
          {/* TV Screen */}
          <Box
            sx={{
              position: 'relative',
              borderRadius: { xs: '6px', md: '8px' },
              overflow: 'hidden',
              aspectRatio: '16/9',
              background: '#000',
            }}
          >
            {/* Star Wars Image */}
            <Box
              component="img"
              src={HERO_IMAGE}
              alt="Star Wars scene on TV with ambient lighting"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
            />

            {/* Screen reflection overlay */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, transparent 50%, rgba(0,0,0,0.1) 100%)',
                pointerEvents: 'none',
              }}
            />
          </Box>

          {/* TV Stand / Base */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mt: { xs: 1, md: 2 },
            }}
          >
            <Box
              sx={{
                width: { xs: '80px', md: '120px' },
                height: { xs: '8px', md: '12px' },
                background: 'linear-gradient(180deg, #2a2a2a 0%, #1a1a1a 100%)',
                borderRadius: '4px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
              }}
            />
          </Box>
        </Box>

        {/* Simulated room lighting effects on sides */}
        <Box
          sx={{
            position: 'absolute',
            left: { xs: '-5%', md: '-8%' },
            top: '10%',
            bottom: '20%',
            width: { xs: '6px', md: '10px' },
            background: 'linear-gradient(180deg, #00cc66 0%, #4488ff 50%, #00cc66 100%)',
            borderRadius: '5px',
            boxShadow: '0 0 40px 15px rgba(0, 200, 100, 0.5), 0 0 80px 30px rgba(68, 136, 255, 0.3)',
            zIndex: 0,
            animation: 'lightPulse 3s ease-in-out infinite',
            '@keyframes lightPulse': {
              '0%, 100%': { opacity: 0.8 },
              '50%': { opacity: 1 },
            },
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            right: { xs: '-5%', md: '-8%' },
            top: '10%',
            bottom: '20%',
            width: { xs: '6px', md: '10px' },
            background: 'linear-gradient(180deg, #4488ff 0%, #00cc66 50%, #4488ff 100%)',
            borderRadius: '5px',
            boxShadow: '0 0 40px 15px rgba(68, 136, 255, 0.5), 0 0 80px 30px rgba(0, 200, 100, 0.3)',
            zIndex: 0,
            animation: 'lightPulse 3s ease-in-out infinite 1.5s',
          }}
        />
      </Box>

      {/* Trust badges */}
      <Typography
        sx={{
          color: 'rgba(255,255,255,0.5)',
          fontSize: '0.9rem',
          letterSpacing: '1px',
        }}
      >
        Works with Google Home & Philips Hue • No subscription required
      </Typography>
    </Container>

    {/* Scroll indicator */}
    <Box
      sx={{
        position: 'absolute',
        bottom: '30px',
        left: '50%',
        transform: 'translateX(-50%)',
        animation: 'bounce 2s infinite',
        '@keyframes bounce': {
          '0%, 100%': { transform: 'translateX(-50%) translateY(0)' },
          '50%': { transform: 'translateX(-50%) translateY(10px)' },
        },
        zIndex: 2,
      }}
    >
      <Box
        sx={{
          width: '30px',
          height: '50px',
          border: '2px solid rgba(255,255,255,0.3)',
          borderRadius: '15px',
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '8px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '6px',
            height: '12px',
            background: '#4488ff',
            borderRadius: '3px',
            animation: 'scroll 2s infinite',
          },
          '@keyframes scroll': {
            '0%': { opacity: 1, top: '8px' },
            '100%': { opacity: 0, top: '25px' },
          },
        }}
      />
    </Box>
  </Box>
);

// Feature card component
const FeatureCard = ({ icon: Icon, title, description, gradient }) => (
  <Card
    sx={{
      background: 'rgba(255,255,255,0.03)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: '20px',
      height: '100%',
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-8px)',
        border: '1px solid rgba(0, 212, 255, 0.3)',
        boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
      },
    }}
  >
    <CardContent sx={{ p: 4 }}>
      <Box
        sx={{
          width: '60px',
          height: '60px',
          borderRadius: '16px',
          background: gradient,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 3,
          boxShadow: `0 10px 30px ${gradient.includes('00d4ff') ? 'rgba(0, 212, 255, 0.3)' : 'rgba(0, 255, 136, 0.3)'}`,
        }}
      >
        <Icon sx={{ fontSize: 30, color: '#fff' }} />
      </Box>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          mb: 2,
          color: '#fff',
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          color: 'rgba(255,255,255,0.6)',
          lineHeight: 1.7,
        }}
      >
        {description}
      </Typography>
    </CardContent>
  </Card>
);

// Features section
const FeaturesSection = () => (
  <Box
    sx={{
      py: 12,
      background: 'linear-gradient(180deg, #0a0a0a 0%, #111118 100%)',
    }}
  >
    <Container maxWidth="lg">
      <Box sx={{ textAlign: 'center', mb: 8 }}>
        <Typography
          variant="overline"
          sx={{
            color: '#00d4ff',
            letterSpacing: '4px',
            fontSize: '0.85rem',
            fontWeight: 600,
          }}
        >
          FEATURES
        </Typography>
        <Typography
          variant="h2"
          sx={{
            fontWeight: 800,
            mt: 2,
            mb: 3,
            fontSize: { xs: '2rem', md: '3rem' },
          }}
        >
          Everything You Need for
          <br />
          <span style={{ color: '#00d4ff' }}>Cinematic Ambiance</span>
        </Typography>
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12} md={4}>
          <FeatureCard
            icon={Mic}
            title="Voice Activated"
            description="Just say 'Hey Google, set the mood for Interstellar' and watch your room transform. Works seamlessly with Google Home."
            gradient="linear-gradient(135deg, #00d4ff 0%, #0099ff 100%)"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FeatureCard
            icon={Palette}
            title="AI Color Extraction"
            description="Our AI analyzes movie posters and scenes to extract the perfect color palette that captures the film's essence."
            gradient="linear-gradient(135deg, #00ff88 0%, #00cc66 100%)"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FeatureCard
            icon={Lightbulb}
            title="Smart Light Control"
            description="Automatically distributes colors across all your Philips Hue lights, creating an immersive surround lighting experience."
            gradient="linear-gradient(135deg, #ff6b6b 0%, #ff4757 100%)"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FeatureCard
            icon={Movie}
            title="Any Movie, Instantly"
            description="Access millions of movies from our database. From classics to the latest releases, we've got the perfect mood for every film."
            gradient="linear-gradient(135deg, #ffd93d 0%, #ffb800 100%)"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FeatureCard
            icon={Settings}
            title="Customizable Targets"
            description="Choose which lights to control—your entire home, specific rooms, or just the lights behind your TV."
            gradient="linear-gradient(135deg, #a855f7 0%, #7c3aed 100%)"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FeatureCard
            icon={PlayArrow}
            title="One-Click Restore"
            description="When movie night is over, instantly restore your lights to their original state with a single tap."
            gradient="linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)"
          />
        </Grid>
      </Grid>
    </Container>
  </Box>
);

// How it works section
const HowItWorksSection = () => (
  <Box
    sx={{
      py: 12,
      background: 'linear-gradient(180deg, #111118 0%, #0a0a0a 100%)',
    }}
  >
    <Container maxWidth="lg">
      <Box sx={{ textAlign: 'center', mb: 8 }}>
        <Typography
          variant="overline"
          sx={{
            color: '#00ff88',
            letterSpacing: '4px',
            fontSize: '0.85rem',
            fontWeight: 600,
          }}
        >
          HOW IT WORKS
        </Typography>
        <Typography
          variant="h2"
          sx={{
            fontWeight: 800,
            mt: 2,
            fontSize: { xs: '2rem', md: '3rem' },
          }}
        >
          Three Simple Steps
        </Typography>
      </Box>

      <Grid container spacing={6} alignItems="center">
        {[
          {
            step: '01',
            title: 'Connect Your Lights',
            description: 'Link your Philips Hue account in seconds. We support all Hue color bulbs, light strips, and lamps.',
          },
          {
            step: '02',
            title: 'Pick a Movie',
            description: 'Search for any movie or use voice commands through Google Home. Our AI instantly generates the perfect color palette.',
          },
          {
            step: '03',
            title: 'Enjoy the Show',
            description: 'Your lights transform to match the movie\'s mood. Sit back, relax, and experience cinema like never before.',
          },
        ].map((item, index) => (
          <Grid item xs={12} md={4} key={index}>
            <Box
              sx={{
                textAlign: 'center',
                position: 'relative',
              }}
            >
              <Typography
                sx={{
                  fontSize: '6rem',
                  fontWeight: 900,
                  color: 'rgba(0, 212, 255, 0.1)',
                  lineHeight: 1,
                  mb: -4,
                  position: 'relative',
                  zIndex: 0,
                }}
              >
                {item.step}
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                {item.title}
              </Typography>
              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.6)',
                  lineHeight: 1.7,
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                {item.description}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Container>
  </Box>
);

// CTA section
const CTASection = ({ onGetStarted }) => (
  <Box
    sx={{
      py: 12,
      background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #0a0a0a 100%)',
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    {/* Background glow */}
    <Box
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '600px',
        height: '600px',
        background: 'radial-gradient(circle, rgba(0, 212, 255, 0.15) 0%, transparent 70%)',
        filter: 'blur(60px)',
      }}
    />

    <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
      <Box sx={{ textAlign: 'center' }}>
        <Typography
          variant="h2"
          sx={{
            fontWeight: 800,
            mb: 3,
            fontSize: { xs: '2rem', md: '3rem' },
          }}
        >
          Ready to Transform Your
          <br />
          <span style={{ color: '#00d4ff' }}>Movie Nights?</span>
        </Typography>
        <Typography
          sx={{
            color: 'rgba(255,255,255,0.6)',
            mb: 4,
            fontSize: '1.1rem',
            maxWidth: '500px',
            mx: 'auto',
          }}
        >
          Join thousands of movie lovers who've upgraded their home cinema experience.
          Free to use, no subscription required.
        </Typography>
        <Button
          variant="contained"
          size="large"
          onClick={onGetStarted}
          sx={{
            background: 'linear-gradient(135deg, #00d4ff 0%, #00ff88 100%)',
            color: '#000',
            fontWeight: 700,
            fontSize: '1.2rem',
            px: 6,
            py: 2,
            borderRadius: '50px',
            textTransform: 'none',
            boxShadow: '0 10px 40px rgba(0, 212, 255, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #00e5ff 0%, #00ff99 100%)',
              boxShadow: '0 15px 50px rgba(0, 212, 255, 0.4)',
              transform: 'translateY(-2px)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          Get Started — It's Free
        </Button>
      </Box>
    </Container>
  </Box>
);

// Footer
const Footer = () => (
  <Box
    sx={{
      py: 6,
      background: '#050505',
      borderTop: '1px solid rgba(255,255,255,0.05)',
    }}
  >
    <Container maxWidth="lg">
      <Grid container spacing={4} alignItems="center">
        <Grid item xs={12} md={4}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(135deg, #00d4ff 0%, #00ff88 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Hue Movie Themes
          </Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.4)',
              textAlign: 'center',
              fontSize: '0.9rem',
            }}
          >
            Works with Philips Hue & Google Home
          </Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.4)',
              textAlign: { xs: 'center', md: 'right' },
              fontSize: '0.9rem',
            }}
          >
            © 2024 Hue Movie Themes
          </Typography>
        </Grid>
      </Grid>
    </Container>
  </Box>
);

// Main Landing component
const Landing = () => {
  const navigate = useNavigate();

  const handleGetStarted = () => {
    navigate('/app');
  };

  return (
    <Box sx={{ minHeight: '100vh', background: '#0a0a0a' }}>
      <HeroSection onGetStarted={handleGetStarted} />
      <FeaturesSection />
      <HowItWorksSection />
      <CTASection onGetStarted={handleGetStarted} />
      <Footer />
    </Box>
  );
};

export default Landing;

